import { Component, input, OnInit, signal, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowStateRulesComponent } from "./workflow.state.rules.component";

@TypeManagerDecorator("workflow-state-rules-step")
@Component({
    selector: "workflow-state-rules-step",
    template: `
        <div class="state-rules-step-container">
            <div class="step-header">
                <h3>State Rules</h3>
                <p class="step-description">
                    Configure rules for each state to control when they are enabled or disabled.
                </p>
            </div>

            <div class="states-list">
                @for (state of getAllStates(); track state.workflowStateId || state.id) {
                    <div class="state-item">
                        <div class="state-header" (click)="toggleStateExpansion(state)">
                            <div class="state-info">
                                <span class="state-name">{{ state.stateName }}</span>
                                <span class="stage-context">in {{ getStageForState(state)?.stageName }}</span>
                                <span class="state-flags">
                                    @if (state.defaultEnabled !== false) {
                                        <span class="flag enabled">Enabled</span>
                                    }
                                </span>
                            </div>
                            <div class="state-actions">
                                <span class="expand-icon" [class.expanded]="isStateExpanded(state)">
                                    {{ isStateExpanded(state) ? "▼" : "▶" }}
                                </span>
                            </div>
                        </div>

                        @if (isStateExpanded(state)) {
                            <div class="state-content">
                                @if (state.workflowStateId && workflowProcess().workflowProcessId) {
                                    <workflow-state-rules
                                        [selectedState]="state"
                                        [workflowProcessId]="workflowProcess().workflowProcessId"
                                    ></workflow-state-rules>
                                } @else {
                                    <div class="rules-unavailable">
                                        <p>State rules will be available after saving the workflow process.</p>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }
            </div>

            @if (getAllStates().length === 0) {
                <div class="no-states">
                    <p>No states defined. Please add states in the previous step.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .state-rules-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .step-description {
            margin: 0;
            color: #666;
            font-size: 14px;
        }

        .states-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .state-item {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
        }

        .state-header {
            padding: 16px;
            background: #f8f9fa;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .state-header:hover {
            background: #e9ecef;
        }

        .state-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .state-name {
            font-weight: 500;
            color: #333;
        }

        .stage-context {
            font-size: 12px;
            color: #666;
            font-style: italic;
        }

        .state-flags {
            display: flex;
            gap: 8px;
        }

        .flag {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .flag.enabled {
            background: #d4edda;
            color: #155724;
        }

        .expand-icon {
            color: #666;
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .state-content {
            padding: 16px;
            border-top: 1px solid #e0e0e0;
        }

        .rules-unavailable {
            text-align: center;
            padding: 40px 20px;
            color: #666;
            background: #f8f9fa;
            border-radius: 6px;
        }

        .no-states {
            text-align: center;
            padding: 40px 20px;
            color: #666;
        }
    `,
    standalone: true,
    imports: [CommonModule, WorkflowStateRulesComponent],
})
export class WorkflowStateRulesStepComponent implements OnInit {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();

    private expandedStates = signal<Set<number | string>>(new Set());

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {}

    getAllStates(): WorkflowTypes.WorkflowState[] {
        const allStates: WorkflowTypes.WorkflowState[] = [];
        this.workflowProcess().stages?.forEach((stage) => {
            if (stage.states) {
                allStates.push(...stage.states);
            }
        });
        return allStates;
    }

    getStageForState(state: WorkflowTypes.WorkflowState): WorkflowTypes.WorkflowStage | null {
        return (
            this.workflowProcess().stages?.find((stage) =>
                stage.states?.some(
                    (s) =>
                        (s.workflowStateId && s.workflowStateId === state.workflowStateId) ||
                        (s.id && s.id === state.id),
                ),
            ) || null
        );
    }

    toggleStateExpansion(state: WorkflowTypes.WorkflowState) {
        const stateKey = state.workflowStateId || state.id!;
        const expanded = this.expandedStates();

        if (expanded.has(stateKey)) {
            expanded.delete(stateKey);
        } else {
            expanded.add(stateKey);
        }

        this.expandedStates.set(new Set(expanded));
    }

    isStateExpanded(state: WorkflowTypes.WorkflowState): boolean {
        const stateKey = state.workflowStateId || state.id!;
        return this.expandedStates().has(stateKey);
    }
}
